export function validateMatrix(data, productId) {
  if (!data || data.productId !== String(productId) || !Number.isInteger(data.count) || data.count < 1 ||
    !Number.isInteger(data.optionCount) || data.optionCount < 1 || data.optionCount > 3 ||
    !Array.isArray(data.ids) || !Array.isArray(data.options) ||
    data.count !== data.ids.length || data.count !== data.options.length) return null;
  if (data.ids.some((id) => !Number.isSafeInteger(id) || id <= 0) || new Set(data.ids).size !== data.count) return null;
  if (data.options.some((values) => !Array.isArray(values) || values.length !== data.optionCount ||
    values.some((value) => typeof value !== 'string' || !value.length))) return null;
  if (new Set(data.options.map((values) => JSON.stringify(values))).size !== data.count) return null;
  return data;
}

export function optionExists(matrix, selection, index, value) {
  return matrix.options.some((values) => values[index] === value &&
    values.slice(0, index).every((previous, position) => previous === selection[position]));
}

export function resolveVariant(matrix, selection, changedIndex = -1) {
  let best = -1;
  let bestScore = -1;
  matrix.options.forEach((values, index) => {
    if (!values.slice(0, changedIndex + 1).every((value, position) => value === selection[position])) return;
    const score = values.reduce((total, value, position) => total + Number(value === selection[position]), 0);
    if (score > bestScore) {
      best = index;
      bestScore = score;
    }
  });
  return best;
}

if (typeof document !== 'undefined') {
  const pickerSelector = 'variant-picker';
  const controlSelector = 'input[type="radio"][data-option-value-id], option[data-option-value-id]';
  const originals = new WeakMap();
  const matrices = new WeakMap();
  let statusId = 0;

  function describe(control, note) {
    const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
      .filter((id) => id !== control.dataset.utDescriptionId);
    if (note) {
      ids.push(note.id);
      control.dataset.utDescriptionId = note.id;
    } else {
      delete control.dataset.utDescriptionId;
    }
    if (ids.length) control.setAttribute('aria-describedby', [...new Set(ids)].join(' '));
    else control.removeAttribute('aria-describedby');
  }

  function statusNote(picker, status, message) {
    if (!message) return null;
    let note = picker.querySelector(`[data-ut-status="${status}"]`);
    if (!note) {
      note = document.createElement('p');
      note.className = 'ut-variant-status';
      note.dataset.utStatus = status;
      note.id = `ut-variant-status-${++statusId}`;
      (picker.querySelector('form') || picker).append(note);
    }
    if (note.textContent !== message) note.textContent = message;
    return note;
  }

  function restore(picker) {
    picker.removeAttribute('data-ut-compatibility');
    for (const control of picker.querySelectorAll(controlSelector)) {
      const original = originals.get(control);
      if (control.hasAttribute('data-ut-exists') && original) {
        control.disabled = original.disabled;
        if (original.aria === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', original.aria);
      }
      control.removeAttribute('data-ut-exists');
      describe(control, null);
      const label = control.closest('label');
      label?.classList.remove('ut-option-incompatible');
      label?.removeAttribute('data-ut-exists');
      label?.querySelector('.ut-variant-cross')?.remove();
    }
    picker.querySelectorAll('.ut-variant-status').forEach((note) => note.remove());
  }

  function context(picker) {
    const script = picker.querySelector('script[data-ut-variant-matrix]');
    if (!script) return null;
    let cached = matrices.get(script);
    if (!cached || cached.text !== script.textContent || cached.productId !== picker.dataset.productId) {
      let matrix;
      try { matrix = validateMatrix(JSON.parse(script.textContent), picker.dataset.productId); } catch { matrix = null; }
      cached = { text: script.textContent, productId: picker.dataset.productId, matrix };
      matrices.set(script, cached);
    }
    const matrix = cached.matrix;
    if (!matrix) return null;
    const groups = Array.from({ length: matrix.optionCount }, () => []);
    for (const control of picker.querySelectorAll(controlSelector)) {
      const connectedUrl = control.dataset.connectedProductUrl;
      if (connectedUrl) {
        try {
          if (new URL(connectedUrl, location.origin).pathname !==
            new URL(picker.dataset.productUrl, location.origin).pathname) return null;
        } catch { return null; }
      }
      const position = Number(control.dataset.inputId?.split('-')[0]) - 1;
      if (!Number.isInteger(position) || !groups[position]) return null;
      groups[position].push(control);
    }
    for (let position = 0; position < groups.length; position++) {
      const values = new Set(matrix.options.map((row) => row[position]));
      const controls = groups[position];
      if (!controls.length) {
        if (values.size !== 1) return null;
      } else if (controls.length !== values.size || controls.some((control) => !values.has(control.value)) ||
        new Set(controls.map((control) => control.value)).size !== values.size) return null;
    }
    const selection = groups.map((controls, position) => controls.length ?
      controls.find((control) => control instanceof HTMLOptionElement ? control.selected : control.checked)?.value :
      matrix.options[0][position]);
    return { matrix, groups, selection };
  }

  function paint(picker, state) {
    const { matrix, groups, selection } = state;
    const soldOutNote = statusNote(picker, 'sold-out', picker.dataset.utSoldOutMessage);
    const incompatibleNote = statusNote(picker, 'incompatible', picker.dataset.utIncompatibleMessage);
    let hasSoldOut = false;
    let hasIncompatible = false;
    picker.setAttribute('data-ut-compatibility', 'ready');
    groups.forEach((controls, position) => controls.forEach((control) => {
      const exists = optionExists(matrix, selection, position, control.value);
      if (!originals.has(control)) originals.set(control, {
        disabled: control.disabled, aria: control.getAttribute('aria-disabled'),
      });
      control.disabled = !exists;
      control.dataset.utExists = String(exists);
      if (exists) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', 'true');
      const soldOut = exists && control.dataset.optionAvailable === 'false';
      hasSoldOut ||= soldOut;
      hasIncompatible ||= !exists;
      describe(control, !exists ? incompatibleNote : soldOut ? soldOutNote : null);
      const label = control.closest('label');
      if (!label) return;
      label.dataset.utExists = String(exists);
      label.classList.toggle('ut-option-incompatible', !exists);
      if (!label.querySelector('.ut-variant-cross')) {
        const cross = document.createElement('span');
        cross.className = 'ut-variant-cross';
        cross.setAttribute('aria-hidden', 'true');
        label.append(cross);
      }
    }));
    if (soldOutNote) soldOutNote.hidden = !hasSoldOut;
    if (incompatibleNote) incompatibleNote.hidden = !hasIncompatible;
  }

  function apply(picker, state, variantIndex, eventTarget) {
    const { matrix, groups } = state;
    const values = matrix.options[variantIndex];
    const changedControls = groups.map((controls, position) => controls.find((control) =>
      control.value === values[position] && !(control instanceof HTMLOptionElement ? control.selected : control.checked)
    )).filter(Boolean).map((control) => control instanceof HTMLOptionElement ? control.parentElement : control);
    const trigger = eventTarget || changedControls.at(-1);
    groups.forEach((controls, position) => {
      const chosen = controls.find((control) => control.value === values[position]);
      if (!chosen) return;
      const target = chosen instanceof HTMLOptionElement ? chosen.parentElement : chosen;
      const changed = chosen instanceof HTMLOptionElement ? !chosen.selected : !chosen.checked;
      if (changed) {
        if (chosen instanceof HTMLOptionElement) target.value = chosen.value;
        else chosen.checked = true;
        if (target !== trigger) picker.updateSelectedOption(target);
      }
      if (chosen instanceof HTMLOptionElement) {
        controls.forEach((control) => {
          if (control.selected !== control.hasAttribute('selected')) control.toggleAttribute('selected', control.selected);
        });
      }
      chosen.dataset.variantId = String(matrix.ids[variantIndex]);
    });
    state.selection = values.slice();
    paint(picker, state);
    return changedControls.length ? trigger : undefined;
  }

  function refresh(picker) {
    if (!picker.isConnected || typeof picker.updateSelectedOption !== 'function') return;
    const state = context(picker);
    if (!state) { restore(picker); return; }
    const exact = state.matrix.options.findIndex((values) => values.every((value, index) => value === state.selection[index]));
    if (exact !== -1) { paint(picker, state); return; }
    const best = resolveVariant(state.matrix, state.selection);
    if (best === -1) { restore(picker); return; }
    const changed = apply(picker, state, best);
    changed?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const picker = target.closest(pickerSelector);
    if (!picker || typeof picker.updateSelectedOption !== 'function') return;
    const state = context(picker);
    if (!state) { restore(picker); return; }
    const selected = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const position = state.groups.findIndex((controls) => controls.includes(selected));
    if (position === -1) return;
    const best = resolveVariant(state.matrix, state.selection, position);
    if (best === -1) {
      event.stopImmediatePropagation();
      refresh(picker);
      return;
    }
    apply(picker, state, best, target);
    if (picker.dataset.templateProductMatch === 'true' && !picker.closest('product-card, quick-add-dialog')) {
      const url = new URL(location.href);
      if (url.searchParams.has('option_values')) {
        url.searchParams.delete('option_values');
        history.replaceState(history.state, '', url);
      }
    }
  }, true);

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root.matches?.(pickerSelector)) refresh(root);
    root.querySelectorAll(pickerSelector).forEach(refresh);
  }

  customElements.whenDefined('variant-picker').then(() => {
    scan(document);
    const observer = new MutationObserver((records) => {
      const pickers = new Set();
      for (const record of records) {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        const picker = target?.closest(pickerSelector);
        if (picker) pickers.add(picker);
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(pickerSelector)) pickers.add(node);
          node.querySelectorAll(pickerSelector).forEach((added) => pickers.add(added));
        }
      }
      pickers.forEach(refresh);
    });
    observer.observe(document.documentElement, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['checked', 'selected', 'data-option-available', 'data-product-id', 'data-option-value-id'],
    });
    document.addEventListener('variant:update', (event) => {
      if (event.target instanceof Element) {
        const picker = event.target.closest(pickerSelector);
        if (picker) refresh(picker);
      }
    }, true);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  });
}
